import { ActionReducerMapBuilder, PayloadAction } from '@reduxjs/toolkit';
import { getModelElementsForModel } from '@/redux/modelElements/modelElements.thunks';
import {
  ModelElementsState,
  SearchModelElementDataState,
} from '@/redux/modelElements/modelElements.types';
import { MODEL_ELEMENTS_STATE_INITIAL_MODEL_MOCK } from '@/redux/modelElements/modelElements.mock';
import { DEFAULT_ERROR } from '@/constants/errors';
import { ModelElement } from '@/types/models';
import { BioEntitySearchedIdObject } from '@/redux/bioEntity/bioEntity.types';

export const getModelElementsReducer = (
  builder: ActionReducerMapBuilder<ModelElementsState>,
): void => {
  builder.addCase(getModelElementsForModel.pending, (state, action) => {
    const modelId = action.meta.arg;
    if (state.data[modelId]) {
      state.data[modelId].loading = 'pending';
    } else {
      state.data[modelId] = { ...MODEL_ELEMENTS_STATE_INITIAL_MODEL_MOCK, loading: 'pending' };
    }
  });
  builder.addCase(getModelElementsForModel.fulfilled, (state, action) => {
    const modelId = action.meta.arg;
    const data = action.payload || [];
    if (state.data[modelId]) {
      state.data[modelId].data = data;
      state.data[modelId].loading = 'succeeded';
    } else {
      state.data[modelId] = { data, loading: 'pending', error: DEFAULT_ERROR };
    }
  });
  builder.addCase(getModelElementsForModel.rejected, (state, action) => {
    const modelId = action.meta.arg;
    if (state.data[modelId]) {
      state.data[modelId].loading = 'failed';
    } else {
      state.data[modelId] = { ...MODEL_ELEMENTS_STATE_INITIAL_MODEL_MOCK, loading: 'failed' };
    }
  });
};

export const setModelElementSearchReducer = (
  state: ModelElementsState,
  action: PayloadAction<SearchModelElementDataState>,
): void => {
  state.search = {
    data: [
      {
        data: [action.payload],
        loading: 'succeeded',
        error: DEFAULT_ERROR,
        searchQueryElement: action.payload.modelElement.id.toString(),
      },
    ],
    loading: 'succeeded',
    error: DEFAULT_ERROR,
  };
};

export const setModelElementSearchByIdsReducer = (
  state: ModelElementsState,
  action: PayloadAction<{
    searchedModelElements: Array<BioEntitySearchedIdObject>;
  }>,
): void => {
  const { searchedModelElements } = action.payload;
  const modelElements: Record<string, Array<{ modelElement: ModelElement; perfect: boolean }>> = {};
  searchedModelElements.forEach(searchedModelElement => {
    const modelElement = state.data[searchedModelElement.modelId]?.data?.find(
      modelElementItem => modelElementItem.id === searchedModelElement.id,
    );

    if (modelElement && modelElements[searchedModelElement.searchQuery]) {
      modelElements[searchedModelElement.searchQuery].push({
        modelElement,
        perfect: searchedModelElement.perfect,
      });
    } else if (modelElement) {
      modelElements[searchedModelElement.searchQuery] = [
        {
          modelElement,
          perfect: searchedModelElement.perfect,
        },
      ];
    }
  });
  Object.entries(modelElements).forEach(([query, modelElementForQuery]) => {
    const stateModelElements = state.search.data.find(
      modelElement => modelElement.searchQueryElement === query,
    );
    if (stateModelElements) {
      stateModelElements.data = modelElementForQuery;
      stateModelElements.loading = 'succeeded';
    } else {
      state.search.data.push({
        data: modelElementForQuery,
        loading: 'succeeded',
        error: DEFAULT_ERROR,
        searchQueryElement: query,
      });
      state.search.loading = 'succeeded';
    }
  });
};

export const setMultipleModelElementSearchReducer = (
  state: ModelElementsState,
  action: PayloadAction<ModelElement[]>,
): void => {
  state.search.data = action.payload.map(modelElement => {
    return {
      data: [
        {
          modelElement,
          perfect: true,
        },
      ],
      loading: 'succeeded',
      error: DEFAULT_ERROR,
      searchQueryElement: modelElement.id.toString(),
    };
  });
  state.search.loading = 'succeeded';
};

export const clearSearchModelElementsReducer = (state: ModelElementsState): void => {
  state.search.data = [];
  state.search.loading = 'idle';
};

export const setLoadingSearchModelElementsReducer = (state: ModelElementsState): void => {
  state.search.loading = 'pending';
};
